import type { FeatureFlagService } from "../../../services/feature-flags";
import type { VersionedStorageService } from "../../../services/versioned-storage";
import { gridPrecisionWarnings } from "../normalization";
import { normalizeOrdersWhenEnabled } from "../normalization-access";
import { parseOrdersWhenEnabled } from "../parser-access";
import { isWarningOrderSource, type OrdersParseTree } from "../parser";
import { hashSourceText, type DigestProvider } from "./hash";
import { loadPreflightRecords, persistUnresolvedTokenLog } from "./persistence";
import type { PreflightCue, PreflightResult, PreflightSession } from "./types";

/**
 * How many blanks have no value.
 *
 * Ruling 63 of 8 October 2026: an unfilled blank never blocks delivery, so
 * what pre-flight needs is a count to show, not a gate to enforce.
 */
export function unfilledBlankCount(
  blanks: PreflightSession["blanks"],
  values: Readonly<Record<string, string>>,
): number {
  return blanks.filter(
    (blank) => (values[blank.id] ?? blank.value).trim() === "",
  ).length;
}

export function missingRequiredFills(
  blanks: PreflightSession["blanks"],
  values: Readonly<Record<string, string>>,
): readonly PreflightSession["blanks"][number][] {
  return blanks.filter(
    (blank) =>
      blank.required && (values[blank.id] ?? blank.value).trim() === "",
  );
}

function itemIds(tree: OrdersParseTree): ReadonlySet<string> {
  return new Set(
    tree.paragraphs.flatMap((paragraph) => [
      ...paragraph.items.map(({ id }) => id),
      ...paragraph.subparagraphs.flatMap((subparagraph) =>
        subparagraph.items.map(({ id }) => id),
      ),
    ]),
  );
}

export function cuePositions(
  tree: OrdersParseTree,
  stored: Readonly<Record<string, string>>,
): readonly PreflightCue[] {
  const validItemIds = itemIds(tree);
  const execution = tree.paragraphs.find(({ id }) => id === "EXECUTION");
  if (execution === undefined) return [];
  const conops = execution.subparagraphs.find(
    ({ id }) => id === "EXECUTION/CONOPS",
  );
  const numbered = conops?.items.find(({ type }) => type === "numbered");
  const mainEffort = conops?.items.find(({ label }) => label === "Main Effort");
  const detected = new Map(
    tree.cues.map((cue) => [cue.name, cue.beforeItemId ?? null]),
  );

  return (
    [
      ["EYES_UP", numbered?.id ?? null],
      ["PENS_RESUME", mainEffort?.id ?? null],
    ] as const
  ).map(([name, proposal]) => {
    const storedNode = stored[name];
    if (storedNode !== undefined && validItemIds.has(storedNode)) {
      return { name, nodeId: storedNode, source: "stored" };
    }
    const detectedNode = detected.get(name);
    if (detectedNode !== undefined) {
      return { name, nodeId: detectedNode, source: "detected" };
    }
    return { name, nodeId: proposal, source: "proposed" };
  });
}

export async function createPreflightSession(
  source: string,
  storage: VersionedStorageService,
  flags: FeatureFlagService,
  digestProvider?: DigestProvider,
): Promise<PreflightResult> {
  if (!flags.isEnabled("preflight")) {
    return {
      ok: false,
      error: {
        code: "FEATURE_DISABLED",
        message: "Orders Reader pre-flight is disabled in this build.",
      },
    };
  }

  const parsed = parseOrdersWhenEnabled(source, flags);
  if (!parsed.ok) {
    return {
      ok: false,
      error: {
        code:
          parsed.error.code === "FEATURE_DISABLED"
            ? "FEATURE_DISABLED"
            : "PARSE_FAILED",
        message: parsed.error.message,
      },
    };
  }

  const fileHash = await hashSourceText(source, digestProvider);
  const records = loadPreflightRecords(storage, fileHash);
  const normalized = normalizeOrdersWhenEnabled(parsed.tree, flags, {
    fills: records.fills,
  });
  if (!normalized.ok) return normalized;

  const uniqueTokens = Array.from(
    new Set(normalized.value.unresolvedTokens.map(({ token }) => token)),
  );
  const logIssues = persistUnresolvedTokenLog(
    storage,
    fileHash,
    normalized.value.logEntries,
  );
  const documentKind = isWarningOrderSource(source)
    ? "warning-order"
    : "orders";
  const itemsById = new Map(
    parsed.tree.paragraphs.flatMap((paragraph) => [
      ...paragraph.items.map((item) => [item.id, item] as const),
      ...paragraph.subparagraphs.flatMap((subparagraph) =>
        subparagraph.items.map((item) => [item.id, item] as const),
      ),
    ]),
  );
  // Where each item sits, in the document's own headings. Taken from the
  // parse tree and never composed, so no heading is invented here.
  const placeById = new Map(
    parsed.tree.paragraphs.flatMap((paragraph) => [
      ...paragraph.items.map(
        (item, at) =>
          [
            item.id,
            `${paragraph.displayHeading}, line ${String(at + 1)}`,
          ] as const,
      ),
      ...paragraph.subparagraphs.flatMap((subparagraph) =>
        subparagraph.items.map(
          (item, at) =>
            [
              item.id,
              `${paragraph.displayHeading} - ${subparagraph.displayHeading}, line ${String(at + 1)}`,
            ] as const,
        ),
      ),
    ]),
  );
  const blanks = normalized.value.blanks.map((blank) => {
    const label = itemsById.get(blank.nodeId)?.label?.trim();
    // What the row calls itself: the item's own label where the document
    // gives one, else the blank's own line with the blank marked. There is
    // no third case -- a marked line is never empty. It used to fall back
    // to the field type and a counter, "control measure 3", whenever the
    // item had no label, which was the ordinary case; forty rows of that
    // read as forty blank rows.
    const named = label !== undefined && label !== "" ? label : blank.context;
    return {
      id: blank.id,
      groupId: blank.groupId,
      fieldType: blank.fieldType,
      prompt: named,
      /* v8 ignore next -- placeById and the blanks come from one traversal */
      location: placeById.get(blank.nodeId) ?? blank.groupId,
      line: blank.context,
      sourceLine: blank.sourceLine,
      width: blank.length,
      typing: blank.typing,
      required: documentKind === "warning-order" || blank.fieldType === "grid",
      value: records.fills[blank.id] ?? "",
    };
  });
  const session: PreflightSession = {
    schemaVersion: 1,
    documentKind,
    fileHash,
    gridPrecisionWarnings: gridPrecisionWarnings(
      parsed.tree,
      parsed.tree.title,
    ),
    parseSummary: {
      title: parsed.tree.title,
      itemCount: itemsById.size,
      paragraphCount: parsed.tree.paragraphs.length,
      warningCount: parsed.tree.warnings.length,
    },
    parserWarnings: parsed.tree.warnings,
    blanks,
    unresolvedTokens: uniqueTokens,
    unresolvedLogEntries: normalized.value.logEntries,
    cues: cuePositions(parsed.tree, records.cuePositions),
    lexiconChoices: records.lexiconChoices,
    calibration: { status: "required" },
    issues: [...records.issues, ...logIssues],
    ordersTree: parsed.tree,
    canStartPlayback: missingRequiredFills(blanks, {}).length === 0,
  };
  return { ok: true, session };
}
